import { Check, Monitor, Moon, Sun } from "lucide-react";
import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { THEME_LABELS, THEME_MODES, type ThemeMode } from "@/lib/theme";
import { useTheme } from "@/lib/theme-context";
import { cn } from "@/lib/utils";

const ICONS: Record<ThemeMode, React.ElementType> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

/** Кнопка темы в шапке: значок текущего выбора, по нажатию — меню из трёх режимов (подписи видны и на
 *  телефоне, где нет всплывающих подсказок). */
export function ThemeToggle(): React.ReactElement {
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const Icon = ICONS[mode];
  const label = `Тема: ${THEME_LABELS[mode].toLowerCase()}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={label} title={label}>
          <Icon className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48 p-1">
        <div role="menu" aria-label="Тема оформления">
          {THEME_MODES.map((m) => {
            const ItemIcon = ICONS[m];
            const active = m === mode;
            return (
              <button
                key={m}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setMode(m);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                  active && "font-medium",
                )}
              >
                <ItemIcon className="size-4 shrink-0" />
                {THEME_LABELS[m]}
                {active && <Check className="ml-auto size-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
